// @flow
import cloneDeep from 'lodash/cloneDeep';

/**
   * Called by the component browser when the user selects a component. Pretty
   * much all the code here is just creating another 'draft' with just the new
   * component added.
   */
const handleSelectComponent = (_slot, _component, targetComponentPosition, insertIndex) => {
  const slot = cloneDeep(_slot);
  const component = cloneDeep(_component);
  const { slotId, components } = slot;

  component._id = generateTempId();

  if (Number.isInteger(insertIndex) && insertIndex >= 0 && insertIndex <= components.length) {
    // Insert between sections (from the "+" between two of them)
    components.splice(insertIndex, 0, component);
  } else if (targetComponentPosition === -1) {
    // Prepend new component to the list
    components.unshift(component);
  } else {
    // Append new component to the list
    components.push(component);
  }

  return {
    slotId,
    components
  };
};

const handleSelectSubcomponent = (_slot, _component, targetComponentPosition, targetSubcomponentParentId, targetSubcomponentPosition) => {
  const slot = cloneDeep(_slot);
  const component = cloneDeep(_component);
  const { slotId, components } = slot;

  component._id = generateTempId();

  // Set subcomponent inside parent component
  const oldComponent = components[targetComponentPosition];

  components[targetComponentPosition] =
    setSubcomponentWithParentId(oldComponent, component, targetSubcomponentParentId, targetSubcomponentPosition);

  return {
    slotId,
    components
  };
};

/**
   * Called when a component is edited via the component editor. The component
   * editor returns a props object with the changes applied.
   */
const handleSaveComponent = (_slot, _component, editingSlotIndex) => {
  const slot = cloneDeep(_slot);
  const component = cloneDeep(_component);
  const { slotId, components } = slot;

  component._id = generateTempId();

  components[editingSlotIndex] = component;

  return {
    slotId,
    components
  };
};

/**
 * Live edits from the inspector: new props for the same section, which keeps
 * its _id (so the selection and the open inspector stay on it). A top-level
 * section takes the props whole, like the edit window's Save; a nested one
 * merges them, like handleSaveSubcomponent.
 */
const handleUpdateComponentProps = (_slot, componentId, nextProps, subcomponentId) => {
  const slot = cloneDeep(_slot);
  const component = slot.components.find(c => c && c._id === componentId);
  if (!component) return slot;
  if (subcomponentId) {
    const sub = getComponentForId(component, subcomponentId);
    if (sub) sub.props = { ...sub.props, ...cloneDeep(nextProps) };
  } else {
    component.props = cloneDeep(nextProps);
  }
  return slot;
};

const handleSaveSubcomponent = (_slot, _component, editingSlotIndex, editingSubcomponentId) => {
  const slot = cloneDeep(_slot);
  const component = cloneDeep(_component);
  const { slotId, components } = slot;

  const oldComponent = slot.components[editingSlotIndex];

  components[editingSlotIndex] = replaceSubcomponentForId(oldComponent, component, editingSubcomponentId);

  return {
    slotId,
    components
  };
};

/**
 * Moves a component to another position in its slot (Outline drag and drop).
 * `to` is the index it should end up at.
 */
const handleReorderComponent = (_slot, from, to) => {
  const slot = cloneDeep(_slot);
  const { components } = slot;
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || from >= components.length) return slot;
  const target = Math.max(0, Math.min(to, components.length - 1));
  if (target === from) return slot;
  const [moved] = components.splice(from, 1);
  components.splice(target, 0, moved);
  return slot;
};

const handleDragAndDrop = (_slot, originalWidgetInfo, targetWidgetInfo) => {
  const slot = cloneDeep(_slot);

  if (originalWidgetInfo?.id === 'NEW') {
    // New component must be created - use component from parsed DataTransfer
    const { component } = originalWidgetInfo;
    // Overwrite with temp ID - required for hosting subcomponents
    component._id = generateTempId();
    return handleDragAndDropDifferentWindow(slot, component, targetWidgetInfo);
  } else {
    return handleDragAndDropSameWindow(slot, originalWidgetInfo, targetWidgetInfo);
  }
}

// Support drag and drop between windows. Only creates new components - no move/delete
const handleDragAndDropDifferentWindow = (slot, originalWidget, targetWidgetInfo) => {
  const { slotId, components } = slot;
  const { id: targetWidgetId, position: targetWidgetPosition } = targetWidgetInfo;

  const eligibleHosts = ['TabMenu', 'FlexibleColumns', 'ResponsiveDisplayContainer', 'Columns'];

  // Short-circuit when attempting to move parent into child
  if (!originalWidget || getComponentForId(originalWidget, targetWidgetId)) {
    throw new Error('Cannot move selected component into target component');
  }

  // Add component to top or bottom slot
  if (['ADD_TOP', 'ADD_BOTTOM'].includes(targetWidgetId)) {
    // Add original widget to front of list / end of list
    if (targetWidgetId === 'ADD_TOP') {
      components.unshift(originalWidget);
    } else {
      components.push(originalWidget);
    }
  } else {
    // Get target widget
    const targetWidget = getComponentForId(slot, targetWidgetId);

    // Short-circuit if targetWidget is not found
    if (!targetWidget) {
      throw new Error('Unable to find target widget');
    }

    // Only move widget if target is a container for other subcomponents
    if (eligibleHosts.includes(targetWidget.widgetId)) {
      // Get the configured number of subcomponents
      let subcomponentCount = 0;

      switch (targetWidget.widgetId) {
        case 'FlexibleColumns':
          subcomponentCount = targetWidget.props.column_layout.count;
          break;
        case 'TabMenu':
          subcomponentCount = targetWidget.props.tabs.length;
          break;
        case 'ResponsiveDisplayContainer':
          subcomponentCount = 1;
          break;
        case 'Columns':
          subcomponentCount = targetWidget.props.columnCount;
          break;
      }

      // Ensure that subcomponents is a property of targetWidget before modifying it
      if (!targetWidget.subcomponents) {
        targetWidget.subcomponents = [];
      }

      const { subcomponents } = targetWidget;

      // Populate array to match configured size
      while (subcomponents.length < subcomponentCount) {
        subcomponents.push(null);
      }

      let swapIndex;

      // targetWidgetPosition should be provided when dragging to a FlexibleColumn or TabMenu
      if (typeof targetWidgetPosition === 'number' && targetWidgetPosition >= 0 && targetWidgetPosition < subcomponentCount && !subcomponents[targetWidgetPosition]) {
        swapIndex = targetWidgetPosition;
      } else {
        // Use first eligible empty space
        swapIndex = subcomponents.indexOf(null);
      }

      // Place original component in the position
      if (swapIndex >= 0 && swapIndex < subcomponentCount) {
        subcomponents[swapIndex] = originalWidget;
      } else {
        throw new Error(`Unable to place component inside "${targetWidget.widgetId}"`);
      }
    }
  }

  return {
    slotId,
    components
  };
}

// Support original drag and drop within the same window
const handleDragAndDropSameWindow = (slot, originalWidgetInfo, targetWidgetInfo) => {
  const { slotId, components } = slot;
  const { id: originalWidgetId } = originalWidgetInfo;
  const { id: targetWidgetId, position: targetWidgetPosition } = targetWidgetInfo;

  const eligibleHosts = ['TabMenu', 'FlexibleColumns', 'ResponsiveDisplayContainer', 'Columns'];

  // Get original widget
  const originalWidget = getComponentForId(slot, originalWidgetId);

  // Short-circuit when attempting to move parent into child
  if (!originalWidget || getComponentForId(originalWidget, targetWidgetId)) {
    throw new Error('Cannot move selected component into target component');
  }

  // Add component to top or bottom slot
  if (['ADD_TOP', 'ADD_BOTTOM'].includes(targetWidgetId)) {
    removeComponentWithId(slot, originalWidgetId);

    // Add original widget to front of list / end of list
    if (targetWidgetId === 'ADD_TOP') {
      components.unshift(originalWidget);
    } else {
      components.push(originalWidget);
    }
  } else {
    // Get target widget
    const targetWidget = getComponentForId(slot, targetWidgetId);

    // Short-circuit if targetWidget is not found
    if (!targetWidget) {
      throw new Error('Unable to find target widget');
    }

    // Only move widget if target is a container for other subcomponents
    if (eligibleHosts.includes(targetWidget.widgetId)) {
      // Remove original widget
      removeComponentWithId(slot, originalWidgetId);

      // Get the configured number of subcomponents
      let subcomponentCount = 0;

      switch (targetWidget.widgetId) {
        case 'FlexibleColumns':
          subcomponentCount = targetWidget.props.column_layout.count;
          break;
        case 'TabMenu':
          subcomponentCount = targetWidget.props.tabs.length;
          break;
        case 'ResponsiveDisplayContainer':
          subcomponentCount = 1;
          break;
        case 'Columns':
          subcomponentCount = targetWidget.props.columnCount;
          break;
      }

      // Ensure that subcomponents is a property of targetWidget before modifying it
      if (!targetWidget.subcomponents) {
        targetWidget.subcomponents = [];
      }

      const { subcomponents } = targetWidget;

      // Populate array to match configured size
      while (subcomponents.length < subcomponentCount) {
        subcomponents.push(null);
      }

      let swapIndex;

      // targetWidgetPosition should be provided when dragging to a FlexibleColumn or TabMenu
      if (typeof targetWidgetPosition === 'number' && targetWidgetPosition >= 0 && targetWidgetPosition < subcomponentCount && !subcomponents[targetWidgetPosition]) {
        swapIndex = targetWidgetPosition;
      } else {
        // Use first eligible empty space
        swapIndex = subcomponents.indexOf(null);
      }

      // Place original component in the position
      if (swapIndex >= 0 && swapIndex < subcomponentCount) {
        subcomponents[swapIndex] = originalWidget;
      } else {
        throw new Error(`Unable to place component inside "${targetWidget.widgetId}"`);
      }
    }
  }

  return {
    slotId,
    components
  };
};

const handleMoveComponent = (_slot, i, direction) => {
  const slot = cloneDeep(_slot);
  const { slotId, components } = slot;

  if ((direction === 'UP' && i === 0) || (direction === 'DOWN' && i === components.length - 1)) {
    throw new Error('Component cannot move in selected direction');
  }

  const toMoveComponent = components.splice(i, 1)[0];

  const newIndex = direction === 'UP' ? i - 1 : i + 1;

  components.splice(newIndex, 0, toMoveComponent);

  return {
    slotId,
    components
  };
};

const handleMoveSubcomponent = (_slot, i, direction, subcomponentId) => {
  const slot = cloneDeep(_slot);
  const { slotId, components } = slot;

  components[i] = moveSubcomponent(direction, components[i], subcomponentId);

  return {
    slotId,
    components
  };
};

const handleCopyComponent = (_slot, i) => {
  const slot = cloneDeep(_slot);
  const { slotId, components } = slot;

  const component = cloneDeep(components[i]);

  updateIdsAfterCopy([component]);

  components.splice(i + 1, 0, component);

  return {
    slotId,
    components
  };
};

const handleCopySubcomponent = (_slot, i, subcomponentId) => {
  const slot = cloneDeep(_slot);
  const { slotId, components } = slot;

  if (subcomponentId) {
    // Subcomponents - recursively update with generated IDs
    components[i] = copySubcomponent(components[i], subcomponentId);
  }

  return {
    slotId,
    components
  };
};

const handleDeleteComponent = (_slot, i) => {
  const slot = cloneDeep(_slot);
  const { slotId, components } = slot;

  components.splice(i, 1);

  return {
    slotId,
    components
  };
};

const handleDeleteSubcomponent = (_slot, i, subcomponentId) => {
  const slot = cloneDeep(_slot);
  const { slotId, components } = slot;

  components[i] = removeComponentWithId(components[i], subcomponentId);

  return {
    slotId,
    components
  };
};

/**
   * Removes metadata from subcomponents
   */
const getSubcomponents = (component) => {
  if (!component) return null;

  const {
    presetId, widgetId, props, subcomponents = []
  } = component;

  const s = subcomponents.map(getSubcomponents);

  return {
    presetId,
    widgetId,
    props,
    subcomponents: s
  };
};

const getComponentForId = (parent, id) => {
  const components = parent.components || parent.subcomponents || [];

  const all = components.filter(n => !!n);

  while (all.length) {
    const sub = all.shift();

    if (sub._id === id) { return sub; }
    if (sub.subcomponents && sub.subcomponents.length) { sub.subcomponents.filter(n => !!n).forEach(x => all.push(x)); }
  }

  return null;
};

const replaceSubcomponentForId = (component, newSubcomponent, id) => {
  const all = component.subcomponents.slice().filter(n => !!n);

  while (all.length) {
    const s = all.shift();

    if (s._id === id) {
      s._id = generateTempId();
      s.props = { ...s.props, ...newSubcomponent.props };
      return component;
    }
    if (s.subcomponents && s.subcomponents.length) { s.subcomponents.slice().filter(n => !!n).forEach(x => all.push(x)); }
  }

  // ID not found
  return component;
};

// Remove component with id from the hierarchy
const removeComponentWithId = (parent, id) => {
  const isRoot = !!parent.components;
  const components = isRoot ? parent.components : parent.subcomponents;

  for (let i = components.length; i >= 0; i--) {
    const sub = components[i];

    if (sub) {
      if (sub._id === id) {
        if (isRoot) {
          // Remove completely from slot
          components.splice(i, 1);
        } else {
          // Replace with empty spot for widget containers
          components[i] = null;
        }
        break;
      } else if (sub.subcomponents && sub.subcomponents.length) {
        removeComponentWithId(sub, id);
      }
    }
  }

  return parent;
};

// Every component needs an _id so that it can be manipulated in the editor
// A temp _id is required until the draft is saved and a new _id is generated by mongo
const generateTempId = () => Math.random().toString(36).substring(2) + (new Date()).getTime().toString(36);

const fillSubcomponentList = (widgetId, subcomponents, props) => {
  const {
    column_layout: columnLayout, // FlexibleColumns
    tabs, // TabMenu
    columnCount // Columns
  } = props;

  if (widgetId === 'FlexibleColumns' && columnLayout) {
    // Ensure there is an item for each column
    while (subcomponents.length < columnLayout.count) {
      subcomponents.push(null);
    }
  } else if (widgetId === 'TabMenu' && tabs) {
    // Ensure there is an item for each tab
    while (subcomponents.length < tabs.length) {
      subcomponents.push(null);
    }
  } else if (widgetId === 'Columns' && columnCount) {
    // Ensure there is an item for each tab
    while (subcomponents.length < columnCount) {
      subcomponents.push(null);
    }
  }

  return subcomponents;
};

const setSubcomponentWithParentId = (parentComponent, newComponent, parentId, position) => {
  // Initialize subcomponents as empty array
  const {
    _id,
    widgetId,
    props
  } = parentComponent;

  let { subcomponents = [] } = parentComponent;

  // Only the top-level component can have a null id
  if (!parentId || _id === parentId) {
    fillSubcomponentList(widgetId, subcomponents, props);

    subcomponents[position] = {
      ...newComponent,
      _id: generateTempId()
    };
  } else {
    // Parent component must be lower in the hierarchy
    subcomponents = subcomponents.map(s => (s ? setSubcomponentWithParentId(s, newComponent, parentId, position) : s));
  }

  return {
    ...parentComponent,
    subcomponents
  };
};

// Assumes parent is 'FlexibleColumns' or 'Columns'
const moveSubcomponent = (direction, parentComponent, subcomponentId) => {
  const {
    subcomponents: subs = [null, null, null, null],
    props: {
      column_layout: columnLayout, // FlexibleColumns
      columnCount // Columns
    }
  } = parentComponent;

  const columns = columnLayout?.count || columnCount

  while (columns > subs.length) {
    subs.push(null);
  }

  // Move subcomponent with id in specified direction
  for (let i = 0; i < subs.length; i++) {
    const s = subs[i];

    if (s != null) {
      if (s._id === subcomponentId) {
        if (direction === 'DOWN' && i < columns - 1) {
          [subs[i], subs[i + 1]] = [subs[i + 1], subs[i]];
        } else if (direction === 'UP' && i > 0) {
          [subs[i], subs[i - 1]] = [subs[i - 1], subs[i]];
        } else {
          throw new Error('Subcomponent cannot move in selected direction');
        }
        return parentComponent;
      }

      if (s.subcomponents && s.subcomponents.length) {
        moveSubcomponent(direction, s, subcomponentId);
      }
    }
  }

  return parentComponent;
};

const copySubcomponent = (parentComponent, subcomponentId) => {
  // Initialize subcomponents as empty array
  const {
    widgetId,
    subcomponents: subs = [],
    props,
    props: {
      column_layout: columnLayout, // FlexibleColumns
      tabs, // TabMenu
      columnCount // Columns
    }
  } = parentComponent;

  const columns = columnLayout?.count || tabs?.length || columnCount;

  fillSubcomponentList(widgetId, subs, props);

  // Copy subcomponent to next open position
  for (let i = 0; i < subs.length; i++) {
    const s = subs[i];

    if (s != null) {
      if (s._id === subcomponentId) {
        if (subs.every((x, index) => {
          // Get max component count for columns/tabs
          const count = columns || 0;

          return x || index >= count;
        })) {
          throw new Error(`No room to copy subcomponent`);
        }

        // Place in next available slot
        const nextAvailableIndex = subs.indexOf(null, i);
        const copyIndex = nextAvailableIndex > 0 ? nextAvailableIndex : subs.indexOf(null);
        const copy = cloneDeep(subs[i]);

        // Insert copy with new _id
        subs[copyIndex] = {
          ...copy,
          _id: generateTempId()
        };
        renewAiScope(subs[copyIndex]);

        // Update _ids of children, if applicable
        const copySubs = subs[copyIndex].subcomponents;

        if (copySubs) {
          updateIdsAfterCopy(copySubs);
        }

        return parentComponent;
      }

      // Look for subcomponent among children
      if (s.subcomponents && s.subcomponents.length) {
        copySubcomponent(s, subcomponentId);
      }
    }
  }

  return parentComponent;
};

// Recursively updates _ids of subcomponents
// A copied AI section gets its own scope class. Sections sharing one share a stylesheet on the
// page (AiGeneratedWidget keys its <style> by the class), so code pasted into the copy would
// restyle the original. The class is a random token: code that names it follows the rename.
const renewAiScope = (c: Object) => {
  const content = c && c.widgetId === 'AiGeneratedWidget' && c.props && c.props.content;
  if (!content || typeof content.scopeClass !== 'string' || !content.scopeClass) return;
  const previous = content.scopeClass;
  const next = `ai-widget-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const swap = v => (typeof v === 'string' ? v.split(previous).join(next) : v);
  c.props = {
    ...c.props,
    content: { ...content, scopeClass: next, html: swap(content.html), css: swap(content.css), js: swap(content.js) }
  };
};

const updateIdsAfterCopy = (components: Object[]) => {
  components.forEach((c) => {
    if (c) {
      c._id = generateTempId();
      renewAiScope(c);

      if (c.subcomponents) {
        updateIdsAfterCopy(c.subcomponents);
      }
    }
  });
};


export default {
  handleSelectComponent,
  handleSelectSubcomponent,
  handleSaveComponent,
  handleSaveSubcomponent,
  handleUpdateComponentProps,
  handleDragAndDrop,
  handleReorderComponent,
  handleMoveComponent,
  handleMoveSubcomponent,
  handleCopyComponent,
  handleCopySubcomponent,
  handleDeleteComponent,
  handleDeleteSubcomponent,
  getSubcomponents,
  getComponentForId
};
